import { Component } from '@theme/component';
import { debounce, mediaQueryLarge, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Marker rendered by snippets/bunnings-header-search.liquid. Everything gated on
 * it is the search panel shell.
 */
const SEARCH_PANEL_SELECTOR = '[data-search-panel="v2"]';
const SEARCH_PANEL_OPEN_CLASS = 'is-open';
/** Enables the CSS transform transition used by the mobile takeover FLIP open. */
const SEARCH_PANEL_ANIMATING_OPEN_CLASS = 'is-animating-open';
/**
 * Holds the mobile takeover's open layout while it animates out. The open class comes
 * off immediately on close so every state guard reads the panel as closed straight away;
 * this one only keeps the pixels around long enough to animate them.
 */
const SEARCH_PANEL_CLOSING_CLASS = 'is-closing';
/** 0 -> 1 dial the takeover's surface, results and close button fade on. */
const TAKEOVER_REVEAL_PROPERTY = '--bunnings-takeover-reveal';
/**
 * FLIP offset for the takeover. Applied through a custom property rather than an inline
 * `transform` because the transform belongs on the pinned `.predictive-search`, while
 * everything the component writes stays on the wrapper it already owns — and a transform
 * on the wrapper would make it the containing block for its own fixed child.
 */
const TAKEOVER_SHIFT_PROPERTY = '--bunnings-takeover-shift';
/** Holds the wrapper's header slot open while its contents are pinned to the viewport. */
const TAKEOVER_SLOT_PROPERTY = '--bunnings-takeover-slot-height';

/** Theme-wide scroll lock hook: `html[scroll-lock] { overflow: hidden }` in base.css. */
const SCROLL_LOCK_ATTRIBUTE = 'scroll-lock';

/** Placeholder the view all locale template reserves for the shopper's query. */
const VIEW_ALL_TERMS_TOKEN = '__TERMS__';

/**
 * Forces the browser to resolve pending style and layout, so a value set immediately
 * before this call becomes the transition's from-state instead of being coalesced away
 * with the value set immediately after it.
 * @param {HTMLElement} element - The element whose styles to commit.
 */
function flushStyles(element) {
  void element.offsetHeight;
}

/**
 * Runs `callback` once every CSS transition inside `element` has settled.
 *
 * Deliberately narrower than the shared `onAnimationEnd`, which waits on every
 * animation in the subtree: the takeover's results carry their own entry keyframes and
 * the panel can hold a loading spinner, so waiting on animations would gate the close
 * teardown — and with it the page scroll lock — on something that may never finish.
 * Transitions are always finite, and these are the only ones the panel runs.
 * @param {HTMLElement} element - The element whose subtree transitions to wait for.
 * @param {() => void} callback - Run once they have all finished or been cancelled.
 */
function onTransitionsEnd(element, callback) {
  const transitions = element
    .getAnimations({ subtree: true })
    .filter((animation) => 'transitionProperty' in animation);

  if (transitions.length === 0) {
    callback();
    return;
  }

  Promise.allSettled(transitions.map((transition) => transition.finished)).then(callback);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [viewAllButton] - The submit button that opens the full results page.
 * @property {HTMLElement} [viewAllLabel] - The text span inside the view all button.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The search panel shell wrapper, or null if this instance is not inside one.
   * @type {HTMLElement | null}
   */
  #searchPanel = null;

  /** @type {MediaQueryList | null} */
  #desktopQuery = null;

  /** True only while this component is the one holding the shared scroll lock. */
  #ownsScrollLock = false;

  /**
   * When true, focus events must not reopen the panel. Armed when we close while the
   * field keeps focus (mobile X) so a window/tab focus restore cannot bounce it back
   * open. Cleared on blur, or on an intentional click reopen.
   */
  #ignoreNextFocus = false;

  /** @type {(() => void) | null} */
  #clearFocusOpenSuppress = null;

  /** The query the shopper actually typed, restored when they navigate off a suggestion. */
  #typedQuery = '';

  /** Guarding BV re-hydration after empty-state morph so a superseded reset cannot clobber a newer one. */
  #bvHydrationRunId = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
      this.#updateViewAllLabel(this.refs.searchInput.value);
    }

    const searchPanel = this.closest(SEARCH_PANEL_SELECTOR);

    if (searchPanel instanceof HTMLElement) {
      this.#searchPanel = searchPanel;
      // Shared with the panel CSS `min-width: 750px` split — keep one source of truth.
      this.#desktopQuery = mediaQueryLarge;
      this.#typedQuery = this.refs.searchInput.value;

      // Pointer and focus exits are handled separately: clicks on non-focusable panel
      // chrome fire focusout with a null relatedTarget, which must not close the panel.
      document.addEventListener('pointerdown', this.#handleOutsidePointerDown, { signal });
      this.addEventListener('focusout', this.#handlePanelFocusOut, { signal });
      this.refs.searchInput.addEventListener('input', this.#handlePanelInput, { signal });
      this.#desktopQuery.addEventListener('change', this.#handleViewportChange, { signal });
      window.addEventListener('resize', this.#handleWindowResize, { passive: true, signal });
      // Safety net for keep-focus closes (mobile X): switching windows does not blur
      // the input, but returning often re-fires focus on it.
      window.addEventListener('blur', this.#handleWindowBlur, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#handleWindowResize.cancel();
    this.#clearFocusOpenSuppress?.();
    this.#clearTakeoverOpenAnimation();
    this.#cancelTakeoverClose();
    this.#releaseTakeoverSlot();
    this.#releaseScrollLock();
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #isSearchPanelOpen() {
    return this.#searchPanel?.classList.contains(SEARCH_PANEL_OPEN_CLASS) ?? false;
  }

  /**
   * Standalone kiosk hides header search entirely; browsable kiosk keeps it but must not
   * get a scrim or a scroll lock fighting the kiosk chrome. Both carry `kiosk-mode`.
   */
  get #isKiosk() {
    return document.documentElement.classList.contains('kiosk-mode');
  }

  /** True below the desktop breakpoint, where the panel becomes a full-page takeover. */
  get #isTakeover() {
    return this.#desktopQuery ? !this.#desktopQuery.matches : false;
  }

  /**
   * Opens the panel. Bound in the panel markup to the field's focus and click.
   * @param {Event} [event] - The triggering focus or click event.
   */
  openSearchPanel = (event) => {
    // Keep-focus closes (mobile X) arm a suppress so window/tab restore focus cannot
    // reopen the panel. A click is an intentional reopen and clears it.
    if (this.#ignoreNextFocus && event?.type !== 'click') return;
    this.#clearFocusOpenSuppress?.();
    this.#openSearchPanel();
  };

  /**
   * Primary mobile close (takeover X). Returns focus to the field so keyboard users are
   * not dropped at the top of the document. Escape only reaches the same
   * `#closeSearchPanel({ blurInput: true })` path once the query is already empty.
   */
  closeSearchPanel = () => {
    this.#closeSearchPanel({ returnFocus: true });
  };

  /**
   * Closes from the desktop scrim or an outside pointer. Blurs the field so focus does
   * not linger and reopen the panel when the window or tab is restored. Mobile has no
   * scrim and no outside hit-target (full-viewport takeover); X / Escape close there.
   */
  dismissSearchPanel = () => {
    this.#closeSearchPanel({ blurInput: true });
  };

  #openSearchPanel() {
    if (!this.#searchPanel || this.#isSearchPanelOpen) return;

    // Reopening mid-close: drop the outgoing animation before measuring, so the field is
    // back at its resting header position and the FLIP below has a real delta to work
    // with rather than measuring the pinned takeover it is about to re-enter.
    this.#cancelTakeoverClose();

    const formHeader = this.querySelector('.predictive-search-form__header');
    const firstTop =
      this.#isTakeover && formHeader instanceof HTMLElement ? formHeader.getBoundingClientRect().top : null;

    if (this.#isTakeover) this.#reserveTakeoverSlot();

    this.#searchPanel.classList.add(SEARCH_PANEL_OPEN_CLASS);
    this.refs.searchInput.setAttribute('aria-expanded', 'true');
    this.#syncPanelAnchor();

    if (this.#isTakeover) this.#lockScroll();

    if (firstTop != null && formHeader instanceof HTMLElement) {
      this.#animateTakeoverOpen(formHeader, firstTop);
    }
  }

  /**
   * FLIP the mobile takeover open so the field eases from its in-header Y to the pinned
   * top instead of jumping, and fade the surface in on the same curve so the branded
   * header does not vanish a frame ahead of the field. Skips under reduced motion.
   *
   * The fade runs even when the field has nowhere to travel (already at the top), since
   * the surface changing over is the bigger part of what read as a cut.
   * @param {HTMLElement} formHeader - The search field row measured before/after open.
   * @param {number} firstTop - `getBoundingClientRect().top` before `is-open` applied.
   */
  #animateTakeoverOpen(formHeader, firstTop) {
    if (!this.#searchPanel || prefersReducedMotion()) return;

    const lastTop = formHeader.getBoundingClientRect().top;
    const delta = Math.max(0, Math.round(firstTop - lastTop));

    const panel = this.#searchPanel;
    this.#clearTakeoverOpenAnimation();

    // Invert: paint the takeover still visually at the in-header field Y, faded out.
    if (delta > 0) panel.style.setProperty(TAKEOVER_SHIFT_PROPERTY, `${delta}px`);
    panel.style.setProperty(TAKEOVER_REVEAL_PROPERTY, '0');

    // Commit that as the from-state before the transition exists, then play to the
    // resting state in the same task. Driving this off requestAnimationFrame instead
    // would leave the takeover stranded invisible on any frame the browser skips.
    flushStyles(panel);

    panel.classList.add(SEARCH_PANEL_ANIMATING_OPEN_CLASS);
    panel.style.removeProperty(TAKEOVER_SHIFT_PROPERTY);
    panel.style.removeProperty(TAKEOVER_REVEAL_PROPERTY);

    onTransitionsEnd(panel, () => {
      panel.classList.remove(SEARCH_PANEL_ANIMATING_OPEN_CLASS);
    });
  }

  /** Cancels an in-flight takeover open animation and clears its dials / class state. */
  #clearTakeoverOpenAnimation() {
    if (!this.#searchPanel) return;

    this.#searchPanel.classList.remove(SEARCH_PANEL_ANIMATING_OPEN_CLASS);
    this.#searchPanel.style.removeProperty(TAKEOVER_SHIFT_PROPERTY);
    this.#searchPanel.style.removeProperty(TAKEOVER_REVEAL_PROPERTY);
  }

  /**
   * Freezes the wrapper's resting height so the header keeps its search row while the
   * contents are pinned to the viewport. Must run before `is-open` lands: the field row
   * about to be pinned is the only thing giving the wrapper height, so measuring after
   * would read zero and the header would collapse anyway.
   */
  #reserveTakeoverSlot() {
    const panel = this.#searchPanel;
    if (!panel) return;

    panel.style.setProperty(TAKEOVER_SLOT_PROPERTY, `${Math.round(panel.getBoundingClientRect().height)}px`);
  }

  /** Hands the wrapper's height back to its own contents once the takeover is gone. */
  #releaseTakeoverSlot() {
    this.#searchPanel?.style.removeProperty(TAKEOVER_SLOT_PROPERTY);
  }

  /**
   * Reverse of the open FLIP: the wrapper has already lost `is-open`, so `is-closing`
   * re-pins the takeover layout for the length of the animation while the field eases
   * back down to its header slot and the surface fades out.
   *
   * The resting position is read after the open class comes off but inside the same
   * synchronous block, so the browser resolves layout twice without ever painting the
   * jump between them.
   * @param {HTMLElement} formHeader - The search field row, measured in both positions.
   * @param {number} pinnedTop - `getBoundingClientRect().top` while still open.
   */
  #animateTakeoverClose(formHeader, pinnedTop) {
    const panel = this.#searchPanel;
    if (!panel) return;

    const delta = Math.max(0, Math.round(formHeader.getBoundingClientRect().top - pinnedTop));

    // Re-pins the takeover, then commits that as the from-state before the transition
    // exists, so the play below animates out rather than snapping.
    panel.classList.add(SEARCH_PANEL_CLOSING_CLASS);
    flushStyles(panel);

    if (delta > 0) panel.style.setProperty(TAKEOVER_SHIFT_PROPERTY, `${delta}px`);
    panel.style.setProperty(TAKEOVER_REVEAL_PROPERTY, '0');

    onTransitionsEnd(panel, () => this.#finishTakeoverClose());
  }

  /** Tears down the takeover once its close animation has played out. */
  #finishTakeoverClose() {
    if (!this.#searchPanel?.classList.contains(SEARCH_PANEL_CLOSING_CLASS)) return;

    this.#cancelTakeoverClose();
    this.#releaseTakeoverSlot();
    this.#releaseScrollLock();
  }

  /**
   * Drops the closing state and its animation dials. Deliberately leaves the scroll lock
   * and the reserved header slot alone: the two callers are the teardown above, which
   * releases both straight after, and a reopen, which needs to keep holding both.
   */
  #cancelTakeoverClose() {
    if (!this.#searchPanel) return;

    this.#searchPanel.classList.remove(SEARCH_PANEL_CLOSING_CLASS);
    this.#searchPanel.style.removeProperty(TAKEOVER_SHIFT_PROPERTY);
    this.#searchPanel.style.removeProperty(TAKEOVER_REVEAL_PROPERTY);
  }

  /**
   * Publishes the field's bottom edge so the desktop panel can be centred on the viewport
   * while still hanging off the field. Centring on the field instead would push a 1100px
   * panel off-screen, because the field sits left of centre in the header.
   *
   * Safe to measure once per open: the field lives in the header's top row, which is
   * sticky at the top of the viewport and unaffected by the bottom row collapsing.
   */
  #syncPanelAnchor() {
    const formHeader = this.querySelector('.predictive-search-form__header');
    if (!this.#searchPanel || !(formHeader instanceof HTMLElement)) return;

    const { bottom } = formHeader.getBoundingClientRect();
    this.#searchPanel.style.setProperty('--bunnings-search-panel-top', `${Math.round(bottom)}px`);
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.returnFocus=false] - Move focus back to the search field.
   * @param {boolean} [options.blurInput=false] - Blur the field (scrim, outside click, Escape).
   */
  #closeSearchPanel({ returnFocus = false, blurInput = false } = {}) {
    if (!this.#searchPanel || !this.#isSearchPanelOpen) return;

    // Capture before blurInput moves focus away.
    const inputKeepsFocus = !blurInput && document.activeElement === this.refs.searchInput;

    const formHeader = this.querySelector('.predictive-search-form__header');
    const animateClose = this.#isTakeover && !prefersReducedMotion() && formHeader instanceof HTMLElement;
    // Measured while the takeover is still pinned; #animateTakeoverClose reads the
    // resting position once the open class is off, and inverts between the two.
    const pinnedTop = animateClose ? formHeader.getBoundingClientRect().top : 0;

    this.#clearTakeoverOpenAnimation();
    this.#cancelTakeoverClose();
    this.#searchPanel.classList.remove(SEARCH_PANEL_OPEN_CLASS);
    this.refs.searchInput.setAttribute('aria-expanded', 'false');

    if (animateClose && formHeader instanceof HTMLElement) {
      // Scroll stays locked until the animation ends, or the page jumps behind the
      // takeover while it is still on screen.
      this.#animateTakeoverClose(formHeader, pinnedTop);
    } else {
      this.#releaseTakeoverSlot();
      this.#releaseScrollLock();
    }

    if (returnFocus) {
      this.#returnFocusToInput();
    } else if (blurInput) {
      this.refs.searchInput.blur();
    } else if (inputKeepsFocus) {
      // Keep-focus close without an explicit blur (e.g. future callers): still suppress
      // focus-driven reopen on window/tab restore.
      this.#suppressFocusOpenUntilBlur();
    }
  }

  /**
   * Focus has to land back on the field without the focus handler treating it as a
   * fresh open. Keep the suppress flag until the field blurs so a window/tab focus
   * restore cannot reopen the panel while the field still holds focus.
   */
  #returnFocusToInput() {
    this.#suppressFocusOpenUntilBlur();
    this.refs.searchInput.focus({ preventScroll: true });
  }

  /**
   * Blocks focus-driven reopens until the shopper leaves the field. Safe to call
   * repeatedly — replaces any previous blur listener rather than stacking them.
   */
  #suppressFocusOpenUntilBlur() {
    this.#ignoreNextFocus = true;

    if (this.#clearFocusOpenSuppress) {
      this.refs.searchInput.removeEventListener('blur', this.#clearFocusOpenSuppress);
    }

    this.#clearFocusOpenSuppress = () => {
      this.#ignoreNextFocus = false;
      this.refs.searchInput.removeEventListener('blur', this.#clearFocusOpenSuppress);
      this.#clearFocusOpenSuppress = null;
    };
    this.refs.searchInput.addEventListener('blur', this.#clearFocusOpenSuppress);
  }

  /**
   * Switching to another OS window leaves the input focused (no blur). Arm the suppress
   * so the focus event that often fires on return cannot reopen a closed panel.
   */
  #handleWindowBlur = () => {
    if (!this.#searchPanel || this.#isSearchPanelOpen) return;
    if (document.activeElement !== this.refs.searchInput) return;

    this.#suppressFocusOpenUntilBlur();
  };

  /**
   * Locks page scroll with the theme-wide `html[scroll-lock]` attribute rather than a
   * bespoke lock, because bunnings-header-scroll.js watches that attribute to stop
   * collapsing the header while an overlay is open. Never locks in kiosk, and never
   * clears a lock another component (e.g. a dialog) already owns.
   */
  #lockScroll() {
    if (this.#isKiosk || this.#ownsScrollLock) return;
    if (document.documentElement.hasAttribute(SCROLL_LOCK_ATTRIBUTE)) return;

    document.documentElement.setAttribute(SCROLL_LOCK_ATTRIBUTE, '');
    this.#ownsScrollLock = true;
  }

  /** Clears the scroll lock only when this component is the one that set it. */
  #releaseScrollLock() {
    if (!this.#ownsScrollLock) return;

    document.documentElement.removeAttribute(SCROLL_LOCK_ATTRIBUTE);
    this.#ownsScrollLock = false;
  }

  #handlePanelInput = () => {
    this.#typedQuery = this.refs.searchInput.value;
    this.#openSearchPanel();
  };

  /**
   * Desktop click-outside. Scrim clicks are bound declaratively in the panel markup.
   * On mobile the open wrapper is `fixed; inset: 0`, so this path never fires there —
   * the X / Escape handlers dismiss the takeover instead.
   * @param {PointerEvent} event - The pointer event.
   */
  #handleOutsidePointerDown = (event) => {
    if (!this.#isSearchPanelOpen) return;

    const target = event.target;
    if (target instanceof Node && this.#searchPanel?.contains(target)) return;

    // Same as scrim dismiss: leave the field unfocused so a later window restore
    // cannot reopen from a lingering caret.
    this.#closeSearchPanel({ blurInput: true });
  };

  /**
   * Keyboard exit (Tab out of the last result).
   * @param {FocusEvent} event - The focus event.
   */
  #handlePanelFocusOut = (event) => {
    if (!this.#isSearchPanelOpen) return;

    const next = event.relatedTarget;
    // Null relatedTarget means a click on non-focusable panel chrome, which
    // #handleOutsidePointerDown already covers. Closing here would fight it.
    if (!(next instanceof Node)) return;
    if (this.#searchPanel?.contains(next)) return;

    this.#closeSearchPanel();
  };

  /** Crossing the breakpoint while open swaps the takeover's scroll lock in or out. */
  #handleViewportChange = () => {
    // Crossing up to desktop mid-close drops the media query the close animation lives
    // in, so finish it here rather than waiting for a transition that will never end.
    if (!this.#isTakeover) this.#finishTakeoverClose();

    if (!this.#isSearchPanelOpen) return;

    if (this.#isTakeover) this.#lockScroll();
    else this.#releaseScrollLock();

    this.#syncPanelAnchor();
  };

  #handleWindowResize = debounce(() => {
    if (this.#isSearchPanelOpen) this.#syncPanelAnchor();
  }, 150);

  /**
   * Mirrors the focused query suggestion into the field, so what Enter will search for is
   * visible and announced. Only query suggestions carry text worth inlining; moving onto a
   * product or back off the list restores what the shopper typed.
   * @param {HTMLElement | null} item - The newly active result, if any.
   */
  #syncInputWithActiveItem(item) {
    if (!this.#searchPanel || !this.#isKeyboardNavigation) return;

    const suggestion = item?.classList.contains('predictive-search-results__card--query')
      ? item.querySelector('[aria-label]')?.getAttribute('aria-label')
      : null;

    this.refs.searchInput.value = suggestion ?? this.#typedQuery;
  }

  /**
   * Keyboard-navigable results, in document order — which both result layouts keep equal
   * to visual order (the panel's two-column typing state renders the left column first).
   * The class names below are the contract shared by sections/predictive-search.liquid and
   * snippets/predictive-search-results-panel.liquid; renaming a container there drops its
   * items out of arrow/Tab navigation.
   * @returns {HTMLElement[]} The navigable result items.
   */
  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter(
        (item) =>
          item instanceof HTMLElement &&
          // Skip CSS-hidden recently-viewed extras on mobile (`display: none`).
          item.getClientRects().length > 0
      );

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.#syncInputWithActiveItem(activeItem);
    // Keep the combobox focused when navigating an active result,
    // but avoid stealing focus when clearing/resetting state (index === -1).
    if (index >= 0) {
      this.refs.searchInput.focus();
    }
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.#searchPanel) {
        // Escape steps back one level at a time rather than dismissing outright.
        // With a query it behaves exactly like Clear (empty state, panel stays open,
        // focus kept); a second Escape from the already-empty state closes and blurs
        // like the scrim. Read the value here because keydown runs before the browser's
        // native clear on input[type="search"].
        if (this.#isSearchPanelOpen && this.refs.searchInput.value.trim()) {
          this.resetSearch();
        } else if (this.#isSearchPanelOpen) {
          this.#closeSearchPanel({ blurInput: true });
        } else if (document.activeElement === this.refs.searchInput) {
          // Closed but still focused (e.g. after mobile X returnFocus): blur so Escape
          // still exits the field.
          this.refs.searchInput.blur();
        }
        return;
      }

      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          searchUrl.searchParams.set('options[prefix]', 'last');
          // Mirror the predictive dropdown's fields option so SKU-only queries also resolve on the full results page.
          searchUrl.searchParams.set(
            'options[fields]',
            'title,product_type,variants.title,vendor,variants.sku'
          );
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#updateViewAllLabel(searchTerm);
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    // Include variants.sku alongside the default fields so SKU lookups match in the dropdown.
    // The `fields` option is all-or-nothing, so the defaults must be re-listed.
    url.searchParams.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor,variants.sku'
    );

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  /**
   * Inject recently-viewed preview cards into the panel empty-state slot.
   * Failures here are non-fatal — the empty state still morphs without the
   * recently-viewed section.
   * @param {Element} emptySectionRoot - Parsed `.predictive-search-empty-section` root.
   */
  async #hydratePanelRecentlyViewed(emptySectionRoot) {
    const slot = emptySectionRoot.querySelector('#predictive-search-recently-viewed');
    const list = emptySectionRoot.querySelector('#predictive-search-recently-viewed-list');
    if (!(slot instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
    if (!recentlyViewedMarkup) return;

    const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
    const cards = parsedRecentlyViewedMarkup.querySelectorAll('.predictive-search-results__card--product');
    if (cards.length === 0) return;

    // Mobile takeover: two full-width rows. Desktop: up to four in the previews grid.
    // Matches the empty-state CSS cap (`nth-child(n + 3)` hidden below 750px).
    const maxCards = this.#isTakeover ? 2 : 4;
    let appended = 0;

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      if (appended >= maxCards) break;
      card.setAttribute('ref', 'recentlyViewedItems[]');
      list.appendChild(card);
      appended += 1;
    }

    slot.hidden = false;
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  /**
   * Swap the view all button between its generic label and the query-scoped one. The
   * button lives in the search shell rather than the morphed results, so the label is
   * built here from the translated template exposed on the element.
   * @param {string} searchTerm - The term the results currently reflect.
   */
  #updateViewAllLabel(searchTerm) {
    const { viewAllButton, viewAllLabel } = this.refs;
    if (!viewAllButton || !viewAllLabel) return;

    const { viewAllTemplate, viewAllDefault } = viewAllButton.dataset;
    const terms = searchTerm.trim();

    viewAllLabel.textContent =
      terms && viewAllTemplate ? viewAllTemplate.replace(VIEW_ALL_TERMS_TOKEN, terms) : (viewAllDefault ?? '');
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#typedQuery = '';
    this.#hideResetButton();
    this.#updateViewAllLabel('');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      // Lift preview cards into the empty-state slot. Never bail before morph —
      // a failed hydrate still leaves popular/categories/fallback.
      await this.#hydratePanelRecentlyViewed(parsedEmptySectionMarkup);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();

    // Empty-state fallback tiles may carry BV placeholders; morph can leave them
    // unhydrated. Dynamic import so a BV module failure cannot prevent this
    // custom element from defining.
    const runId = ++this.#bvHydrationRunId;
    import('@theme/bv-inline-ratings')
      .then(({ scheduleBvMissedHydrationRecovery }) => {
        scheduleBvMissedHydrationRecovery({
          runId,
          getCurrentRunId: () => this.#bvHydrationRunId,
          root: predictiveSearchResults,
        });
      })
      .catch((error) => {
        console.error('[predictive-search] bv-inline-ratings unavailable', error);
      });
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
